import {
  type EditingOp,
  type EditingOutputNode,
  filterNodes,
  isBinaryOp,
  isBracketsOp,
  isDotChainedOp,
  type Node,
  type NodeOrVoidNode,
  type OpStore,
  type WeaveInterface,
} from '@wandb/weave/core';
import {useEffect, useRef} from 'react';

import {type EventRecorder} from '../panellib/useEventRecorder';
import type * as Table from './tableState';

/**
 * Shape of the table a user is looking at, recorded with the VIEW event.
 *
 * Booleans only, and deliberately no column names or expressions: these answer
 * "what kind of table is this" without putting user data in analytics.
 */
export type TableShape = {
  isGrouped: boolean;
  isSorted: boolean;
  hasFilter: boolean;
  isJoined: boolean;
  isConcatenated: boolean;
  hasDerivedColumns: boolean;
};

/**
 * Op names down the expression's main chain, outermost first.
 *
 * Only the chain matters: the op that produced the rows a table is showing is on
 * it, so a `joinAll` buried in a column expression or a filter lambda is not
 * this table's join. Following the chain is also bounded by its depth rather
 * than by the size of the whole graph.
 */
const chainOpNames = (input: Node): string[] => {
  const names: string[] = [];
  let node: Node | undefined = input;
  while (node != null && node.nodeType === 'output') {
    names.push(node.fromOp.name);
    node = Object.values(node.fromOp.inputs)[0];
  }
  return names;
};

/** Every op in an expression, including the ops inside lambdas. */
const expressionOps = (expr: NodeOrVoidNode): EditingOp[] =>
  filterNodes(expr, node => node.nodeType === 'output').map(
    node => (node as EditingOutputNode).fromOp
  );

/**
 * Whether a column combines a row-field read with a binary operator.
 *
 * Both parts are required: the row-field read establishes a dependency on the
 * row, and the binary operator establishes a transformation or comparison.
 *
 * Examples:
 * - `row["loss"]`: false, direct field read.
 * - `row["run"].name`: false, chained field read.
 * - `1 + 1`: false, binary operator with no row-field dependency.
 * - `row["loss"] * 100`: true.
 * - `row["scores"].filter(score => score > 0.5)`: true, including the binary
 *   operator inside the lambda.
 */
const isDerivedColumn = (
  selectFn: NodeOrVoidNode,
  opStore: OpStore
): boolean => {
  const ops = expressionOps(selectFn);
  return (
    ops.some(op => isBinaryOp(op, opStore)) &&
    ops.some(op => isBracketsOp(op, opStore) || isDotChainedOp(op, opStore))
  );
};

export function summarizeTableShape(
  tableState: Table.TableState,
  input: Node,
  weave: WeaveInterface
): TableShape {
  const chain = chainOpNames(input);
  const opStore = weave.client.opStore;

  return {
    isGrouped: tableState.groupBy.length > 0,
    isSorted: tableState.sort.length > 0,
    hasFilter: tableState.preFilterFunction.nodeType !== 'void',
    // `joinAll` is the only join we expect: the `joined-table` and
    // `partitioned-table` types are deprecated at write time, so their
    // `normalizeTableLike` ops don't appear on tables people log today.
    isJoined: chain.some(name => name.endsWith('joinAll')),
    isConcatenated: chain.some(name => name.endsWith('concat')),
    hasDerivedColumns: Object.values(tableState.columnSelectFunctions).some(
      selectFn => isDerivedColumn(selectFn, opStore)
    ),
  };
}

/**
 * Records VIEW once per mount, tagged with the table's shape.
 *
 * The inputs are read through a ref so the effect doesn't depend on them:
 * depending on the table state directly would re-fire VIEW on every grouping or
 * sort change.
 */
export function useRecordTableView(
  recordEvent: EventRecorder,
  inputs: {
    tableState: Table.TableState | undefined;
    input: Node;
    weave: WeaveInterface;
  }
) {
  const inputsRef = useRef(inputs);
  // oxlint-disable-next-line react/refs
  inputsRef.current = inputs;

  useEffect(() => {
    const {tableState, input, weave} = inputsRef.current;
    recordEvent(
      'VIEW',
      tableState == null
        ? undefined
        : summarizeTableShape(tableState, input, weave)
    );
  }, [recordEvent]);
}
